import { parsePartialJson, type UIMessage } from '@ai-sdk/ui-utils';

import type {
  Chunk,
  PartialArgsBuffer,
  Part,
  ReasoningPayload,
  TextPart,
  TextPayload,
  ToolCallDeltaPayload,
  ToolCallFinalPayload,
  ToolCallStartPayload,
  ToolPart,
  ToolPartUpsert,
  ToolResultPayload,
} from './mastra-stream-accumulator.types';

export type { Chunk } from './mastra-stream-accumulator.types';

export class MastraStreamAccumulator {
  private readonly partialArgs = new Map<string, PartialArgsBuffer>();
  private buffer: UIMessage[];

  constructor(initial: readonly UIMessage[] = []) {
    this.buffer = [...initial];
  }

  public get messages(): readonly UIMessage[] {
    return this.buffer;
  }

  public apply(input: { type: string; payload?: unknown }): readonly UIMessage[] {
    // Stream chunks arrive as parsed JSON — cast at the boundary so the
    // internal switch can narrow `payload` per chunk type.
    const chunk = <Chunk>input;
    switch (chunk.type) {
      case 'start':
        this.appendAssistantMessage(chunk.payload?.messageId ?? crypto.randomUUID());
        break;
      case 'text-start':
      case 'text-delta':
      case 'text-end':
        this.applyTextChunk(chunk.type, chunk.payload);
        break;
      case 'reasoning-delta':
        this.applyReasoningDelta(chunk.payload);
        break;
      case 'tool-call-input-streaming-start':
        this.applyToolCallStart(chunk.payload);
        break;
      case 'tool-call-delta':
        this.applyToolCallDelta(chunk.payload);
        break;
      case 'tool-call':
        this.applyToolCallFinal(chunk.payload);
        break;
      case 'tool-result':
        this.applyToolResult(chunk.payload);
        break;
      case 'tool-call-input-streaming-end':
      case 'step-start':
      case 'step-finish':
      case 'finish':
      case 'error':
      default:
        // No state mutation. Final tool args arrive on `tool-call`.
        break;
    }
    return this.buffer;
  }

  public setMessages(messages: readonly UIMessage[]): void {
    this.buffer = [...messages];
  }

  public reset(): void {
    this.buffer = [];
    this.partialArgs.clear();
  }

  private appendAssistantMessage(id: string): UIMessage {
    const message: UIMessage = { id, role: 'assistant', content: '', parts: [] };
    this.buffer.push(message);
    return message;
  }

  private lastAssistantMessage(): UIMessage {
    for (let index = this.buffer.length - 1; index >= 0; index--) {
      const message = this.buffer[index]!;
      if (message.role === 'assistant') return message;
    }
    return this.appendAssistantMessage(crypto.randomUUID());
  }

  private applyTextChunk(
    chunkType: 'text-start' | 'text-delta' | 'text-end',
    payload: TextPayload | undefined,
  ): void {
    const message = this.lastAssistantMessage();
    const parts = (message.parts ??= []);
    const partId = payload?.id ?? '';
    const isMatchingText = (part: Part): part is TextPart =>
      part.type === 'text' && (<TextPart>part).id === partId;
    let textPart = parts.find(isMatchingText);
    if (!textPart) {
      textPart = { type: 'text', text: '', id: partId };
      parts.push(textPart);
    }
    if (chunkType === 'text-delta') {
      textPart.text += payload?.text ?? '';
    }
    message.content = parts
      .filter((part): part is TextPart => part.type === 'text')
      .map(part => part.text)
      .join('');
  }

  private applyReasoningDelta(payload: ReasoningPayload | undefined): void {
    if (!payload?.text) return;
    const message = this.lastAssistantMessage();
    const parts = (message.parts ??= []);
    // Open a new reasoning segment if the last part isn't an open reasoning
    // part — this preserves order: reasoning between tool calls becomes its
    // own segment instead of merging with prior reasoning.
    const lastPart = parts[parts.length - 1];
    let reasoningPart = lastPart?.type === 'reasoning' ? lastPart : undefined;
    if (!reasoningPart) {
      reasoningPart = { type: 'reasoning', reasoning: '', details: [] };
      parts.push(reasoningPart);
    }
    reasoningPart.reasoning += payload.text;
  }

  private applyToolCallStart(payload: ToolCallStartPayload | undefined): void {
    if (!payload) return;
    this.partialArgs.set(payload.toolCallId, { text: '', toolName: payload.toolName });
    this.upsertToolPart({
      toolCallId: payload.toolCallId,
      toolName: payload.toolName,
      args: undefined,
      state: 'partial-call',
      providerMetadata: payload.providerMetadata,
    });
  }

  private applyToolCallDelta(payload: ToolCallDeltaPayload | undefined): void {
    if (!payload) return;
    const partial = this.partialArgs.get(payload.toolCallId);
    if (!partial) return;
    partial.text += payload.argsTextDelta ?? '';
    const { value: parsedArgs } = parsePartialJson(partial.text);
    this.upsertToolPart({
      toolCallId: payload.toolCallId,
      toolName: partial.toolName,
      args: parsedArgs,
      state: 'partial-call',
      providerMetadata: payload.providerMetadata,
    });
  }

  private applyToolCallFinal(payload: ToolCallFinalPayload | undefined): void {
    if (!payload) return;
    this.partialArgs.delete(payload.toolCallId);
    this.upsertToolPart({
      toolCallId: payload.toolCallId,
      toolName: payload.toolName,
      args: payload.args,
      state: 'call',
      providerMetadata: payload.providerMetadata,
    });
  }

  private applyToolResult(payload: ToolResultPayload | undefined): void {
    if (!payload) return;
    this.upsertToolPart({
      toolCallId: payload.toolCallId,
      toolName: payload.toolName ?? '',
      args: payload.args,
      state: 'result',
      result: payload.result ?? payload.output,
      providerMetadata: payload.providerMetadata,
    });
  }

  private upsertToolPart(upsert: ToolPartUpsert): void {
    const message = this.lastAssistantMessage();
    const parts = (message.parts ??= []);
    const existingPart = parts.find(
      (part): part is ToolPart =>
        part.type === 'tool-invocation' && part.toolInvocation?.toolCallId === upsert.toolCallId,
    );
    const invocation: ToolPart['toolInvocation'] =
      upsert.state === 'result'
        ? {
            state: 'result',
            toolCallId: upsert.toolCallId,
            toolName: upsert.toolName,
            args: upsert.args,
            result: upsert.result,
          }
        : {
            state: upsert.state,
            toolCallId: upsert.toolCallId,
            toolName: upsert.toolName,
            args: upsert.args,
          };
    if (existingPart) {
      existingPart.toolInvocation = invocation;
      if (upsert.providerMetadata !== undefined) {
        existingPart.providerMetadata = upsert.providerMetadata;
      }
      return;
    }
    const part: ToolPart = {
      type: 'tool-invocation',
      toolInvocation: invocation,
      ...(upsert.providerMetadata !== undefined
        ? { providerMetadata: upsert.providerMetadata }
        : {}),
    };
    parts.push(part);
  }
}
